import { createFileRoute, Link } from "@tanstack/react-router";
import primaPietra from "@/assets/scalinata-pietra-prima-pulizia-brescia.webp.asset.json";
import dopoPietra from "@/assets/scalinata-pietra-dopo-pulizia-brescia.webp.asset.json";
import pietraMosaico from "@/assets/pavimento-pietra-ciottoli-mosaico-lombardia.webp.asset.json";
import cortilePietra from "@/assets/cortile-pietra-lavato-lombardia.webp.asset.json";
import pavimentoPorfido from "@/assets/libera4.webp.asset.json";
import { BeforeAfter } from "@/components/BeforeAfter";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks } from "@/components/Social";
import {
  ANNI_ESPERIENZA,
  ANNO_FONDAZIONE,
  EMAIL,
  TEL_ANTONIO,
  TEL_LUCA,
  WHATSAPP_URL,
  province,
} from "@/lib/site";

const URL_PAGINA = "https://artigianidelcotto.com/trattamento-marmo-pietre-graniglia";
const TITOLO = "Pulizia pavimenti in marmo, pietra e graniglia | Lombardia";
const DESCRIZIONE =
  "Lavaggio professionale e trattamento antimacchia per pavimenti in marmo, pietra naturale e graniglia (seminato veneziano, palladiana) in tutta la Lombardia. Preventivo gratuito: 339 751 7813.";
const IMMAGINE = `https://artigianidelcotto.com${dopoPietra.url}`;

const servizi = [
  {
    titolo: "Pulizia pavimenti in marmo",
    testo:
      "Lavaggio profondo di pavimenti e scale in marmo con detergenti a pH controllato: togliamo aloni di detersivo, sporco incrostato e la patina opaca lasciata dai prodotti sbagliati, senza corrodere la pietra.",
    voci: ["Pavimenti e scale in marmo", "Marmo di Botticino e Carrara", "Davanzali e soglie", "Recupero dopo lavori edili"],
  },
  {
    titolo: "Trattamento antimacchia marmo e pietra",
    testo:
      "Dopo il lavaggio applichiamo un protettivo idro-oleorepellente specifico per materiali calcarei: acqua, vino, caffè e olio restano in superficie e si puliscono facilmente, con finitura naturale che non ingiallisce.",
    voci: ["Protettivo idro-oleorepellente", "Effetto naturale o ravvivante", "Cucine, bagni e ingressi", "Rinnovo periodico della protezione"],
  },
  {
    titolo: "Graniglia, seminato veneziano e palladiana",
    testo:
      "I pavimenti in graniglia, seminato alla veneziana e palladiana si opacizzano con cere vecchie e detersivi aggressivi. Li laviamo, li deceriamo dove serve e li proteggiamo, riportando in evidenza il disegno originale.",
    voci: ["Graniglia e cementine", "Seminato alla veneziana", "Pavimenti alla palladiana", "Deceratura e riprotezione"],
  },
];

const passaggi = [
  {
    titolo: "Sopralluogo e prova",
    testo:
      "Riconosciamo il materiale e facciamo una prova su una piccola area nascosta: solo così scegliamo il detergente e il protettivo giusti. Preventivo scritto e gratuito.",
  },
  {
    titolo: "Lavaggio controllato",
    testo:
      "Marmo, pietra e graniglia sono calcarei: niente acidi aggressivi. Usiamo prodotti a pH compatibile e monospazzola con dischi adatti alla superficie.",
  },
  {
    titolo: "Asciugatura",
    testo:
      "La pietra deve essere completamente asciutta prima della protezione, altrimenti il protettivo non penetra e resta in superficie.",
  },
  {
    titolo: "Protezione antimacchia",
    testo:
      "Applichiamo il protettivo a più mani fino a saturazione, poi rimuoviamo l'eccesso. Ti lasciamo le indicazioni per la pulizia quotidiana.",
  },
];

const galleria = [
  { src: pavimentoPorfido.url, alt: "Pavimento in porfido e pietra naturale lavato e protetto in Lombardia" },
  { src: pietraMosaico.url, alt: "Pavimento esterno in pietra e ciottoli con mosaico a stella, lavato e protetto in Lombardia" },
  { src: cortilePietra.url, alt: "Cortile in pietra naturale lavato e trattato in Lombardia" },
];

const faq = [
  {
    q: "Fate anche levigatura, lucidatura o cristallizzazione del marmo?",
    a: "No. Ci occupiamo esclusivamente di lavaggio e trattamento protettivo. Non eseguiamo levigatura, lucidatura a piombo né cristallizzazione: se il tuo pavimento ha bisogno di quelle lavorazioni te lo diciamo subito e in modo trasparente.",
  },
  {
    q: "Come si pulisce un pavimento in marmo senza rovinarlo?",
    a: "Il marmo è calcareo: aceto, anticalcare, candeggina e detergenti acidi lo corrodono e lo rendono opaco. Serve un detergente neutro o specifico per pietre naturali, e per lo sporco profondo un lavaggio professionale con monospazzola.",
  },
  {
    q: "Il trattamento antimacchia cambia l'aspetto del marmo?",
    a: "Con la finitura naturale no: il protettivo penetra nel materiale e resta invisibile. Se preferisci un effetto più caldo e saturo possiamo usare una versione ravvivante, che scurisce leggermente il colore.",
  },
  {
    q: "Trattate anche i pavimenti alla veneziana e alla palladiana?",
    a: "Sì, per la parte di pulizia e protezione. Laviamo il seminato, rimuoviamo le cere vecchie ingiallite e applichiamo un protettivo antimacchia. La rilevigatura del seminato non rientra nei nostri servizi.",
  },
  {
    q: "Quanto costa il trattamento di un pavimento in marmo o graniglia?",
    a: "Dipende dai metri quadri, dallo stato della superficie e dal tipo di protettivo. Facciamo sopralluogo e preventivo gratuiti in tutta la Lombardia: chiama o scrivi su WhatsApp al 339 751 7813.",
  },
];

export const Route = createFileRoute("/trattamento-marmo-pietre-graniglia")({
  component: MarmoPietreGraniglia,
  head: () => ({
    meta: [
      { title: TITOLO },
      { name: "description", content: DESCRIZIONE },
      { property: "og:title", content: TITOLO },
      { property: "og:description", content: DESCRIZIONE },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_PAGINA },
      { property: "og:image", content: IMMAGINE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMMAGINE },
      { name: "geo.region", content: "IT-25" },
    ],
    links: [{ rel: "canonical", href: URL_PAGINA }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HomeAndConstructionBusiness",
              "@id": "https://artigianidelcotto.com/#azienda",
              name: "Artigiani del Cotto",
              url: "https://artigianidelcotto.com/",
              telephone: "+39 339 751 7813",
              email: EMAIL,
              image: IMMAGINE,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Torbole Casaglia",
                addressRegion: "BS",
                addressCountry: "IT",
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
            },
            {
              "@type": "WebPage",
              "@id": `${URL_PAGINA}#pagina`,
              url: URL_PAGINA,
              name: TITOLO,
              description: DESCRIZIONE,
              inLanguage: "it-IT",
              primaryImageOfPage: { "@type": "ImageObject", url: IMMAGINE },
              isPartOf: {
                "@type": "WebSite",
                "@id": "https://artigianidelcotto.com/#sito",
                url: "https://artigianidelcotto.com/",
                name: "Artigiani del Cotto",
              },
              breadcrumb: { "@id": `${URL_PAGINA}#briciole` },
            },
            {
              "@type": "Service",
              "@id": `${URL_PAGINA}#servizio`,
              name: "Pulizia e trattamento di marmo, pietre e graniglia",
              serviceType: "Lavaggio e trattamento protettivo antimacchia di pavimenti in marmo, pietra naturale e graniglia",
              category: "Manutenzione e trattamento pavimenti",
              description: DESCRIZIONE,
              url: URL_PAGINA,
              image: [IMMAGINE],
              provider: { "@id": "https://artigianidelcotto.com/#azienda" },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: `${URL_PAGINA}#contatti`,
                description: "Sopralluogo e preventivo gratuiti in tutta la Lombardia.",
              },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Servizi su marmo, pietre e graniglia",
                itemListElement: servizi.map((s) => ({
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: s.titolo, description: s.testo },
                })),
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${URL_PAGINA}#faq`,
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              inLanguage: "it-IT",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL_PAGINA}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://artigianidelcotto.com/" },
                { "@type": "ListItem", position: 2, name: "Marmo, pietre e graniglia", item: URL_PAGINA },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function MarmoPietreGraniglia() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>Marmo, pietre e graniglia</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Dal {ANNO_FONDAZIONE} in tutta la Lombardia</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Pulizia e trattamento di pavimenti in marmo, pietra e graniglia
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              Lavaggio professionale e trattamento antimacchia per marmo, pietra naturale, graniglia,
              seminato veneziano e palladiana. Solo pulizia e protezione: niente levigatura,
              lucidatura a piombo o cristallizzazione. {ANNI_ESPERIENZA} di esperienza.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#contatti"
                className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
              >
                Richiedi un preventivo gratuito
              </a>
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
              >
                Chiama Antonio: 339 751 7813
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="eyebrow">Perché serve un lavaggio professionale</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Il marmo non si pulisce come una piastrella.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Marmo, travertino, pietra naturale e graniglia sono materiali calcarei: reagiscono agli
                acidi. Aceto, anticalcare e detergenti sbagliati li corrodono, lasciano aloni opachi e
                aprono la porosità, così le macchie entrano più in fretta.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Noi laviamo con prodotti a pH compatibile e monospazzola, poi applichiamo un protettivo
                idro-oleorepellente che ferma acqua, olio e vino in superficie. Il risultato è un pavimento
                pulito, uniforme e molto più facile da mantenere ogni giorno.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Pulizia profonda di marmo, pietra naturale e graniglia",
                  "Trattamento antimacchia idro-oleorepellente",
                  "Rimozione di cere vecchie e ingiallite dal seminato",
                  "Abitazioni, uffici, negozi, scale e parti comuni",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="self-start">
              <BeforeAfter
                before={primaPietra.url}
                after={dopoPietra.url}
                label="Scalinata in pietra naturale — prima e dopo il lavaggio"
                aspectClass="aspect-[3/4]"
              />
            </div>
          </div>
        </section>

        <section id="servizi" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">I nostri servizi</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Cosa facciamo su marmo, pietre e graniglia.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {servizi.map((s, i) => (
                <article
                  key={s.titolo}
                  className="surface-warm flex flex-col rounded-sm border border-border p-7 transition-transform hover:-translate-y-1"
                >
                  <span className="font-display text-sm font-semibold text-copper">0{i + 1}</span>
                  <h3 className="mt-3 text-xl font-semibold">{s.titolo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                  <ul className="mt-6 space-y-2 border-t border-border pt-5 text-sm">
                    {s.voci.map((v) => (
                      <li key={v} className="flex gap-2">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-copper" />
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Non eseguiamo levigatura, lucidatura a piombo o cristallizzazione del marmo: il nostro
              lavoro è il lavaggio e la protezione delle superfici. Se cerchi il trattamento del cotto,
              vedi <Link to="/pulizia-lavaggio-cotto" className="text-primary underline underline-offset-4">pulizia e lavaggio del cotto</Link>{" "}
              o <Link to="/trattamento-cotto-esterno" className="text-primary underline underline-offset-4">trattamento cotto esterno</Link>.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Il metodo</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quattro passaggi, materiale per materiale.
          </h2>
          <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {passaggi.map((p, i) => (
              <li key={p.titolo} className="border-t-2 border-copper pt-5">
                <span className="font-display text-sm font-semibold text-copper">Fase {i + 1}</span>
                <h3 className="mt-2 text-lg font-semibold">{p.titolo}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.testo}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="lavori" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Alcuni lavori</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Superfici lavate e protette in Lombardia.
            </h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {galleria.map((g) => (
                <img
                  key={g.alt}
                  src={g.src}
                  alt={g.alt}
                  width={1200}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  className="aspect-4/3 w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
                />
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Domande frequenti</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quello che ci chiedono più spesso.
          </h2>
          <div className="mt-10 divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none text-base font-semibold marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="gradient-warm py-16 text-background">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow text-sand">Zone servite</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">
              Marmo, pietre e graniglia in tutta la Lombardia.
            </h2>
            <p className="mt-4 max-w-2xl text-background/85">
              Con sede a Torbole Casaglia (Brescia) interveniamo in tutte le province lombarde.
              Sopralluogo gratuito e preventivo senza impegno.
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {province.map((p) => (
                <li
                  key={p}
                  className="rounded-xs border border-background/25 px-3 py-1.5 text-sm text-background/90"
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Preventivo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Mandaci due foto della superficie.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Bastano un paio di foto e i metri quadri indicativi per una prima stima.
                Sopralluogo e preventivo sono gratuiti e senza impegno.
              </p>
              <div className="mt-8 space-y-3">
                <a
                  href={`tel:${TEL_ANTONIO}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Antonio</span>
                  <span className="text-primary">+39 339 751 7813</span>
                </a>
                <a
                  href={`tel:${TEL_LUCA}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Luca</span>
                  <span className="text-primary">+39 351 720 4660</span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Email</span>
                  <span className="text-primary">{EMAIL}</span>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
